import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Collapse,
  Box,
  Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const GROUPS = {
  PRICE: ['LTP', 'Change', 'Change%', 'Close'],
  VOLUME: ['Volume'],
  // GREEKS: ['IV', 'Rho', 'Vega', 'Theta', 'Gamma', 'Delta']
};

export default function ColumnFilter({ selectedColumns, setSelectedColumns, sx }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({
    PRICE: true,
    VOLUME: true,
    GREEKS: true
  });

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleGroupExpand = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleSelectAll = () => {
    const allCols = Object.values(GROUPS).flat().map(c => c.toLowerCase());
    const isAllSelected = allCols.every(col => selectedColumns.includes(col));

    if (isAllSelected) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(allCols);
    }
  };

  const handleGroupToggle = (group) => {
    const groupCols = GROUPS[group].map(c => c.toLowerCase());
    const isAllGroupSelected = groupCols.every(col => selectedColumns.includes(col));
    
    if (isAllGroupSelected) {
      setSelectedColumns(prev => prev.filter(c => !groupCols.includes(c)));
    } else {
      setSelectedColumns(prev => {
        const newCols = new Set([...prev, ...groupCols]);
        return Array.from(newCols);
      });
    }
  };

  const handleColumnToggle = (group, col) => {
    const lowerCol = col.toLowerCase();
    setSelectedColumns(prev => {
      if (prev.includes(lowerCol)) {
        return prev.filter(c => c !== lowerCol);
      } else {
        return [...prev, lowerCol];
      }
    });
  };

  const isAllSelected = Object.values(GROUPS).flat().map(c => c.toLowerCase()).every(col => 
    selectedColumns.includes(col)
  );

  return (
    <>
      <Button
        onClick={handleClick}
        variant="outlined"
        endIcon={<ArrowDropDownIcon sx={{ ml: -0.5 }} />}
        sx={{
          height: { xs: 24, sm: 26, md: 28 },
          minWidth: { xs: 65, sm: 75, md: 80 },
          fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
          borderRadius: { xs: 1, sm: 1.5 },
          textTransform: 'none',
          borderColor: open ? theme.palette.primary.main : theme.palette.divider,
          color: theme.palette.text.primary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: 'transparent',
          },
          ...sx
        }}
      >
        Column
      </Button>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 200,
            maxHeight: 500,
            mt: 1,
            '& .MuiMenuItem-root': {
              minHeight: 36,
              py: 0.5,
              px: 1,
            },
            '& .MuiCheckbox-root': {
              p: 0.5,
              mr: 1,
            }
          }
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={handleSelectAll} sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 0.5 }}>
          <Checkbox checked={isAllSelected} size="small" />
          <ListItemText primary="Select All" primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
        </MenuItem>

        {Object.keys(GROUPS).map((group) => {
          const isGroupSelected = GROUPS[group].every(col => selectedColumns.includes(col.toLowerCase()));
          
          return (
            <React.Fragment key={group}>
              <MenuItem sx={{ display: 'flex', justifyContent: 'space-between', pr: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }} onClick={() => handleGroupToggle(group)}>
                  <Checkbox checked={isGroupSelected} size="small" />
                  <ListItemText primary={group} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
                </Box>
                <Box 
                  onClick={(e) => { e.stopPropagation(); toggleGroupExpand(group); }} 
                  sx={{ 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center',
                    p: 0.5,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: theme.palette.action.hover }
                  }}
                >
                  {expandedGroups[group] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
              </MenuItem>
              
              <Collapse in={expandedGroups[group]} timeout="auto" unmountOnExit>
                {GROUPS[group].map((col) => (
                  <MenuItem 
                    key={col} 
                    sx={{ pl: 4 }}
                    onClick={() => handleColumnToggle(group, col)}
                  >
                    <Checkbox checked={selectedColumns.includes(col.toLowerCase())} size="small" />
                    <ListItemText primary={col} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                  </MenuItem>
                ))}
              </Collapse>
            </React.Fragment>
          );
        })}
      </Menu>
    </>
  );
}
